import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question:
      "Is the end of a long-term unmarried relationship really comparable to divorce?",
    answer:
      "Yes. A six-year relationship that involved a shared home, shared finances, and a shared future involves the same psychological losses as a marriage — the emergency contact, the co-signer, the person who knew your history. In some ways it's harder, because there's no legal ritual, no social script, and often less support. Friends may not have understood the relationship as 'real' without a wedding. The isolation after an adult breakup is often more complete than after a divorce.",
  },
  {
    question:
      "Why is a breakup in your 30s and 40s a particular risk for depression?",
    answer:
      "The stakes are higher. A breakup at this age collides with fertility timelines, a mortgage, a career that doesn't leave time to start over, and a sense that your peers have settled. The relationship was also load-bearing — it carried more than romance. And by this age, many people have already had at least one depressive episode or have been managing depression on medication for years. The breakup doesn't create the vulnerability; it removes the thing that was holding it in check.",
  },
  {
    question: "When does grief after a breakup become depression?",
    answer:
      "Grief comes in waves, it's clearly about the person, and it responds to comfort and time. Depression is what happens when the wave never recedes. If you've had low mood most of the day, nearly every day, for two weeks or longer, the sadness has spread from the breakup to work, friends, and the future generally, nothing brings pleasure, sleep is broken, you can't concentrate, and you feel worthless — it isn't heartbreak anymore.",
  },
  {
    question: "What if therapy and medication haven't worked after a breakup?",
    answer:
      "If you've had two or more adequate trials of antidepressants without remission, that's treatment-resistant depression. The chance that the next medication produces remission is around 14 percent. SAINT works on the brain circuitry directly — using an fMRI to map your individual brain networks and stimulating the underactive mood-regulation region — which is why it can work when medications haven't. And it takes five days instead of six to nine weeks, which fits inside one week of PTO.",
  },
  {
    question: "Do I have to stop my medications to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Nothing enters the bloodstream, and there's no interaction with existing medications. Never change or stop medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title:
      "Divorce and Depression: When the Hardest Year of Your Life Doesn't End",
    href: "/divorce-and-depression",
    description:
      "Why divorce is a cascade of losses that can trigger or worsen depression, and what to do when it doesn't lift.",
  },
  {
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    description:
      "When functioning hides the severity of depression — and why partial relief isn't the goal.",
  },
  {
    title:
      "When a Breakup Doesn't Get Better: Heartbreak, Depression, and the Line Between Them",
    href: "/when-a-breakup-doesnt-get-better",
    description:
      "Why a breakup at 18–25 can trigger a first depressive episode, and what to do when it doesn't fade.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function LongTermRelationshipBreakup() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "Ending a Long-Term Relationship in Your 30s & 40s | Neuro Wellness",
        description:
          "The end of a long-term unmarried relationship carries a specific, overlooked risk for depression. Why it's harder than people assume, when grief becomes depression, and what actually works.",
        canonical: "/ending-a-long-term-relationship-in-your-30s-and-40s",
        keywords:
          "long-term relationship breakup depression, breakup 30s 40s, unmarried breakup grief, treatment-resistant depression breakup, SAINT TMS adults, depression after relationship ending, grief vs depression, TMS for working adults",
      }}
      badge="For Adults"
      heroTitle="The Breakup Nobody Takes Seriously: Ending a Long-Term Relationship in Your 30s and 40s"
      heroSubtitle="The end of a six-year relationship that never involved a wedding gets a shrug and 'at least you weren't married.' But the loss is real, the grief is real, and the depression that can follow is a medical condition — not a character flaw."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                There's a hierarchy of sympathy for relationship endings.
                Divorce gets acknowledged. A widow gets casseroles. But the end
                of a six-year relationship that never involved a wedding gets a
                shrug and "at least you weren't married."
              </p>
              <p>
                If you're 34 and the person you've lived with since 27 just
                moved out, you know how hollow that sounds. You lost a partner,
                a home, a shared future, possibly a plan for children, and most
                of your social life. The fact that there's no paperwork doesn't
                make it smaller. In some ways it makes it harder, because
                there's no ritual, no legal process, and no social script for
                the grief.
              </p>
              <p>
                This article is about breakups in adulthood, why they carry a
                specific risk for depression that's often overlooked, and what
                to do when the low doesn't lift.
              </p>
            </div>
          ),
        },
        {
          heading: "What's different about breakups in your 30s and 40s",
          body: (
            <div className="space-y-5">
              <p>
                <strong>The stakes are higher.</strong> In your twenties, a
                breakup is painful but the future still looks wide open. In your
                thirties and forties, the ending of a long relationship collides
                with a clock. Fertility timelines, a mortgage, a career that
                doesn't leave time to start over, a sense that your peers have
                settled and you're being sent back to the beginning.
              </p>
              <p>
                <strong>The relationship was load-bearing.</strong> A long adult
                partnership carries more than romance. It's your emergency
                contact, your co-signer, the person who knew your history
                without being told. When it ends, the infrastructure of your
                life goes with it.
              </p>
              <p>
                <strong>The support is thinner.</strong> Friends are married,
                busy with kids, or were mutual and now awkward. Family may not
                have understood the relationship as "real" if it wasn't a
                marriage. The isolation after an adult breakup is often more
                complete than after one in college.
              </p>
              <p>
                <strong>You may already be on the edge.</strong> By this age,
                many people have already had at least one depressive episode, or
                have been quietly managing anxiety or depression on a medication
                for years. A breakup doesn't create the vulnerability. It
                removes the thing that had been holding it in check.
              </p>
            </div>
          ),
        },
        {
          heading: "When grief becomes depression",
          body: (
            <div className="space-y-5">
              <p>
                The loss of a long relationship produces real grief, and grief
                is not a disorder. It comes in waves, it's clearly about the
                person, it responds to comfort and time, and it slowly makes
                room for a new life.
              </p>
              <p>
                Depression is what happens when the wave never recedes. The
                warning signs:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Low mood most of the day, nearly every day, for two weeks or
                  longer
                </li>
                <li>
                  The sadness has spread from the breakup to work, friends, and
                  the future generally
                </li>
                <li>
                  Nothing brings pleasure, including things unrelated to the
                  relationship
                </li>
                <li>
                  Sleep is broken or excessive, appetite is off, energy is gone
                </li>
                <li>
                  You can't concentrate at work and are starting to fall behind
                </li>
                <li>
                  You feel worthless or excessively guilty, replaying what you
                  did wrong
                </li>
                <li>
                  You've thought that it wouldn't matter if you weren't here
                </li>
              </ul>
              <p>
                If it's been three months and this describes you, it isn't
                heartbreak anymore.
              </p>
            </div>
          ),
        },
        {
          heading: "The treatment path, and where it stalls",
          body: (
            <div className="space-y-5">
              <p>
                The first steps are the right ones: a therapist, and if symptoms
                are moderate to severe, an antidepressant from your doctor. For
                many people that works.
              </p>
              <p>
                But for a meaningful share, it doesn't. You try one medication
                for eight weeks, get partial relief, and switch. Try another,
                wait another eight weeks, still not well. Add something for
                sleep. Add something for the anxiety that came with it. A year
                after the breakup you're on three or four medications,
                functioning, and not okay.
              </p>
              <p>
                That's treatment-resistant depression. And the odds that the
                next medication produces remission are around 14 percent.
              </p>
            </div>
          ),
        },
        {
          heading: "Why the brain, not just the chemistry, needs treating",
          body: (
            <div className="space-y-5">
              <p>
                Antidepressants work on brain chemistry. But depression is also
                a circuitry problem. The region behind the forehead that
                regulates mood, motivation, and attention becomes underactive
                under prolonged stress and loss, and its connections to the rest
                of the mood network weaken. Medication changes the chemical
                environment around that circuit. It doesn't directly retrain it.
              </p>
              <p>
                Transcranial magnetic stimulation (TMS) does. It uses focused
                magnetic pulses through a coil on the scalp to stimulate the
                underactive region directly. No anesthesia, nothing entering
                your bloodstream, nothing that interacts with what you're
                already taking.
              </p>
            </div>
          ),
        },
        {
          heading: "Why SAINT fits an adult's life",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's cleared for adults with
                major depressive disorder who haven't gotten adequate relief
                from antidepressants in their current episode.
              </p>
              <p>
                The problem with standard TMS for a working adult has always
                been the schedule: 30 to 36 daily visits over six to nine weeks.
                SAINT takes five days. Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  history, your medication trials, and what's changed, and
                  confirms you're a candidate. The initial consultation is a
                  Zoom call; as steps evolve, you'll also have the option to
                  meet with our psychiatrist by Zoom web conference or in
                  person. The mandatory in-person steps include fMRI mapping and
                  the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil there for every
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. One week of PTO.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                In the clinical trials behind SAINT's clearance, 79 percent of
                participants reached remission, with an average time to
                remission of 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events. Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort. You don't have to stop your current
                medications. SAINT isn't appropriate for people with
                non-removable metal in or near the head or an implanted device.
              </p>
            </div>
          ),
        },
        {
          heading: "Something no one may have said to you",
          body: (
            <div className="space-y-5">
              <p>
                Your loss was real. The relationship was real. And the
                depression that followed is a medical condition, not a character
                flaw or a failure to get over it. You're allowed to want to be
                well, not just functional, and you're allowed to pursue the
                treatment most likely to get you there.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If the breakup was months ago and you're still not yourself, and
                you've already tried therapy and medication, call us.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We offer private outpatient
                care with a dedicated care team and weekend availability. We'll
                review your history and tell you honestly whether SAINT is a
                fit. Insurance coverage varies by plan; our team can help you
                understand your options, including prior authorization and
                self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
